/**
 * Pack 8.A — Locale provider.
 *
 * Reads the active locale from the tenant settings endpoint
 * (`/api/tenant/locale-settings`, exposed by `server/auth-routes.ts`) — which
 * is itself backed by `resolveTenantLocale()` and the cache invalidation
 * channel landed in Pack 6 #996. The provider then:
 *
 *   1. Pushes the locale into the i18next instance so `t(...)` resolves
 *      against the right resource bundle.
 *   2. Sets `document.documentElement.lang` and `dir` so RTL languages
 *      (Arabic, Hebrew, Persian, Urdu, Pashto, Sindhi, Yiddish) flip the
 *      whole layout.
 *   3. Exposes `useLocale()` for callers that want the resolved tag, the
 *      tenant's IANA time-zone, an `isRtl` flag, and pre-bound formatters
 *      so they do not have to thread the locale through every prop.
 *
 * Layouts (`PartnerLayout`, `SuperAdminLayout`) used to set the document
 * `dir` themselves; that responsibility now lives here so we have a single
 * source of truth.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";

import {
  formatCurrency as fmtCurrency,
  formatDate as fmtDate,
  formatDateTime as fmtDateTime,
  formatNumber as fmtNumber,
  formatPercent as fmtPercent,
  formatRelativeTime as fmtRelativeTime,
  formatTime as fmtTime,
  isRtlLocale,
} from "@/lib/i18n/formatters";
import { setLocale as setI18nLocale } from "@/lib/i18n";
import { useAuth } from "@/lib/auth-context";

const DEFAULT_LOCALE = "en-GB";
const DEFAULT_TIMEZONE = "UTC";

interface LocaleSettingsResponse {
  timezone: string | null;
  locale: string | null;
  resolvedTimezone: string;
  resolvedLocale: string;
  /** BCP-47 tag stored on the user row (`users.preferredLanguage`). When set
   *  it takes precedence over the tenant locale so each user can override the
   *  organisation default with their own preferred language. */
  userPreferredLocale?: string | null;
}

export interface LocaleContextValue {
  /** Active BCP 47 locale tag (e.g. `en-GB`, `ar-SA`). */
  locale: string;
  /** Tenant IANA time zone (e.g. `Europe/London`, `Asia/Riyadh`). */
  timeZone: string;
  /** True when the locale's primary script is right-to-left. */
  isRtl: boolean;
  /** Override the locale at runtime (e.g. dev-only RTL toggle). */
  setLocaleOverride: (next: string | null) => void;
  /** Whether an override is currently in effect. */
  hasOverride: boolean;
  /** Pre-bound `Intl.*` formatters honouring the active locale + tz. */
  formatDate: (value: Date | string | number | null | undefined, opts?: Intl.DateTimeFormatOptions) => string;
  formatTime: (value: Date | string | number | null | undefined, opts?: Intl.DateTimeFormatOptions) => string;
  formatDateTime: (value: Date | string | number | null | undefined, opts?: Intl.DateTimeFormatOptions) => string;
  formatNumber: (value: number | null | undefined, opts?: Intl.NumberFormatOptions) => string;
  formatCurrency: (value: number | null | undefined, currency: string, opts?: Intl.NumberFormatOptions) => string;
  formatPercent: (value: number | null | undefined, opts?: Intl.NumberFormatOptions) => string;
  formatRelativeTime: (value: number, unit: Intl.RelativeTimeFormatUnit, opts?: Intl.RelativeTimeFormatOptions) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

interface LocaleProviderProps {
  children: ReactNode;
  /**
   * If `true`, skip the `/api/tenant/locale-settings` query and use the
   * defaults. Useful for stories, tests, and unauthenticated routes.
   */
  skipFetch?: boolean;
}

export function LocaleProvider({ children, skipFetch = false }: LocaleProviderProps) {
  const overrideRef = useRef<string | null>(null);
  const { isAuthenticated, currentUser } = useAuth();

  // Pack 8.A — locale endpoint requires an authenticated session AND an active
  // tenant. Firing the query before auth resolves (or for Super Admins, who
  // have platform-scope identity and no tenant binding) caused the 401-handler
  // in `getQueryFn` to bounce users to /auth/expired on the very first hit to
  // any non-`/auth/*` path. Gate the fetch on the same `isAuthenticated`
  // signal that CompanyProvider uses, and skip when the active session has no
  // active tenant (e.g. Super Admins, mid-`switchTenant` transitions).
  const hasActiveTenant = !!currentUser?.activeTenant?.id;
  const shouldFetch = !skipFetch && isAuthenticated && hasActiveTenant;

  const { data } = useQuery<LocaleSettingsResponse>({
    queryKey: ["/api/tenant/locale-settings"],
    enabled: shouldFetch,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Resolve effective locale — priority order (Task #2054 — TRI-076):
  //   1. explicit override (dev preview, tests) wins
  //   2. user's own preferred language (`users.preferredLanguage` in DB)
  //   3. tenant-resolved locale from the API
  //   4. browser fallback ➜ DEFAULT_LOCALE
  const userPreferredLocale = data?.userPreferredLocale ?? null;
  const resolvedLocale = userPreferredLocale ?? data?.resolvedLocale ?? DEFAULT_LOCALE;
  const resolvedTimezone = data?.resolvedTimezone ?? DEFAULT_TIMEZONE;
  const locale = overrideRef.current ?? resolvedLocale;
  const isRtl = isRtlLocale(locale);

  useEffect(() => {
    setI18nLocale(locale);
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
      document.documentElement.dir = isRtl ? "rtl" : "ltr";
    }
  }, [locale, isRtl]);

  const setLocaleOverride = useCallback((next: string | null) => {
    overrideRef.current = next;
    // Force re-render via i18next ➜ react-i18next subscription.
    setI18nLocale(next ?? resolvedLocale);
    if (typeof document !== "undefined") {
      const effective = next ?? resolvedLocale;
      document.documentElement.lang = effective;
      document.documentElement.dir = isRtlLocale(effective) ? "rtl" : "ltr";
    }
  }, [resolvedLocale]);

  const value = useMemo<LocaleContextValue>(() => ({
    locale,
    timeZone: resolvedTimezone,
    isRtl,
    setLocaleOverride,
    hasOverride: overrideRef.current !== null,
    formatDate: (value, opts) => fmtDate(value, locale, { timeZone: resolvedTimezone, ...(opts ?? {}) }),
    formatTime: (value, opts) => fmtTime(value, locale, { timeZone: resolvedTimezone, ...(opts ?? {}) }),
    formatDateTime: (value, opts) => fmtDateTime(value, locale, { timeZone: resolvedTimezone, ...(opts ?? {}) }),
    formatNumber: (value, opts) => fmtNumber(value, locale, opts),
    formatCurrency: (value, currency, opts) => fmtCurrency(value, locale, currency, opts),
    formatPercent: (value, opts) => fmtPercent(value, locale, opts),
    formatRelativeTime: (value, unit, opts) => fmtRelativeTime(value, unit, locale, opts),
  }), [locale, resolvedTimezone, isRtl, setLocaleOverride]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

/**
 * Read the active locale + helpers. Falls back to a defaults-only
 * implementation when no provider is mounted (mostly to keep storybook /
 * isolated tests from crashing).
 */
export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (ctx) return ctx;

  // Defaults-only fallback (no provider in scope).
  const locale = DEFAULT_LOCALE;
  const timeZone = DEFAULT_TIMEZONE;
  return {
    locale,
    timeZone,
    isRtl: false,
    setLocaleOverride: () => {},
    hasOverride: false,
    formatDate: (value, opts) => fmtDate(value, locale, { timeZone, ...(opts ?? {}) }),
    formatTime: (value, opts) => fmtTime(value, locale, { timeZone, ...(opts ?? {}) }),
    formatDateTime: (value, opts) => fmtDateTime(value, locale, { timeZone, ...(opts ?? {}) }),
    formatNumber: (value, opts) => fmtNumber(value, locale, opts),
    formatCurrency: (value, currency, opts) => fmtCurrency(value, locale, currency, opts),
    formatPercent: (value, opts) => fmtPercent(value, locale, opts),
    formatRelativeTime: (value, unit, opts) => fmtRelativeTime(value, unit, locale, opts),
  };
}
